import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { memo, useEffect, useRef, useState } from "react";

const getBearing = (prev, next) => {
  if (!prev || !next) return 0;

  const lat1 = prev.lat * (Math.PI / 180);
  const lat2 = next.lat * (Math.PI / 180);
  const dLon = (next.long - prev.long) * (Math.PI / 180);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const DriverMarkers = ({ drivers, onSelect, followTargetId, map }) => {
  const markersRef = useRef({});
  const [clusterer, setClusterer] = useState(null);

  useEffect(() => {
    if (!map || !window.google) return undefined;

    const instance = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 80 }),
    });
    setClusterer(instance);

    return () => {
      instance.clearMarkers();
      Object.values(markersRef.current).forEach(({ marker }) => {
        marker.map = null;
      });
      markersRef.current = {};
      setClusterer(null);
    };
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    Object.keys(markersRef.current).forEach((id) => {
      if (drivers.some((d) => d.driverId === id)) return;
      const { marker } = markersRef.current[id];
      clusterer.removeMarker(marker);
      marker.map = null;
      delete markersRef.current[id];
    });
  }, [drivers, clusterer]);

  useEffect(() => {
    if (!map || !clusterer) return;
    if (!window.google?.maps?.marker?.AdvancedMarkerElement) return;

    drivers.forEach((driver) => {
      const lat = Number(driver.location?.lat);
      const long = Number(driver.location?.long);
      if (!Number.isFinite(lat) || !Number.isFinite(long)) return;

      if (!markersRef.current[driver.driverId]) {
        const img = document.createElement("img");
        img.style.width = "55px";
        img.style.transition = "transform .25s linear";
        img.style.willChange = "transform";

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat, lng: long },
          content: img,
          title: driver?.driverName,
        });

        marker.addListener("click", () => onSelect(driver.driverId));

        markersRef.current[driver.driverId] = { marker, img };
        clusterer.addMarker(marker);
      }

      const { marker, img } = markersRef.current[driver.driverId];

      let icon = "assets/markers/pickup_driver.png";
      if (driver.orders?.length > 0) {
        icon = driver.orders.some((o) => o.status === "5")
          ? "assets/markers/en_route_driver.png"
          : "assets/markers/pickup_driver.png";
      }
      const nextSrc = `${process.env.REACT_APP_PUBLIC_URL}${icon}`;
      if (img.getAttribute("src") !== nextSrc) img.src = nextSrc;

      img.style.transform = `rotate(${getBearing(
        driver.prevLocation,
        driver.location
      )}deg)`;

      marker.position = { lat, lng: long };

      if (followTargetId === driver.driverId) {
        map.panTo({ lat, lng: long });
      }
    });
  }, [drivers, clusterer, followTargetId, map, onSelect]);

  return null;
};

export default memo(DriverMarkers);